import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { createContextStoreV2 } from "@faire/web/common/hooks/ContextStore/createContextStoreV2";

import {
  MainStreamFetchState,
  createMainStreamFetchState,
} from "./MainStreamFetchState";
import { MessengerPollingFallback } from "./MessengerPollingFallback";

/**
 * Note: All hooks referencing the messenger store must support not having a `MessengerContext` provided, as there is no
 * such context in the visitor portal. The most correct way to implement a `useCallback` exported
 * from this file is to do an early return if `MessengerContext` is not provided (with a default
 * value like `false` for something with a return value like `hasConversationWithBrand`.)
 */

export type MessengerContext = {
  hasInitialized: boolean;
  setHasInitialized: (hasInitialized: boolean) => void;

  user?: IUser;
  setUser: (user: IUser) => void;

  brand?: IGetMessengerConversationsResponse.ISimpleBrand;

  brandContext?: IGetMessengerConversationsResponse.ISimpleBrand;
  setBrandContext: (
    updater: (
      currentBrandContext?: IGetMessengerConversationsResponse.ISimpleBrand
    ) => IGetMessengerConversationsResponse.ISimpleBrand | undefined
  ) => void;

  latestMessageUpdatedAt?: number;
  setLatestMessageUpdatedAt: (
    latestMessageUpdatedAt: number | undefined
  ) => void;

  retailerContext?: IRetailer;
  setRetailerContext: (
    updater: (currentRetailerContext?: IRetailer) => IRetailer | undefined
  ) => void;

  query: string;
  setQuery: (query: string) => void;

  lastSearchQuery?: string;
  setLastSearchQuery: (lastSearchQuery: string | undefined) => void;

  searchResults?: Set<string>;
  setSearchResults: (searchResults: Set<string> | undefined) => void;

  allMainStreamFetchState: MainStreamFetchState;
  setAllMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  unreadOnlyMainStreamFetchState: MainStreamFetchState;
  setUnreadOnlyMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  starredByBrandOnlyMainStreamFetchState: MainStreamFetchState;
  setStarredByBrandOnlyMainStreamFetchState: (
    updater: (state: MainStreamFetchState) => MainStreamFetchState
  ) => void;

  currentConversationIdentifier?: string;
  setCurrentConversationIdentifier: (
    currentConversationIdentifier: string | undefined
  ) => void;

  isLoadingCount: number;
  incrementIsLoadingCount: () => void;
  decrementIsLoadingCount: () => void;

  showOnlyUnreadConversations: boolean;
  setShowOnlyUnreadConversations: (
    showOnlyUnreadConversations: boolean
  ) => void;

  showOnlyStarredByBrandConversations: boolean;
  setShowOnlyStarredByBrandConversations: (
    showOnlyStarredByBrandConversations: boolean
  ) => void;

  hiddenConversations: string[];

  totalUnreadConversations?: number;
  setTotalUnreadConversations: (
    newTotalUnreadConversations:
      | number
      | undefined
      | ((
          previousTotalUnreadConversations: number | undefined
        ) => number | undefined)
  ) => void;

  totalStarredByBrandConversations?: number;
  setTotalStarredByBrandConversations: (
    totalStarredByBrandConversations: number | undefined
  ) => void;

  wasConnected: boolean;
  setWasConnected: (wasConnected: boolean) => void;

  pollingFallback?: MessengerPollingFallback;
  setPollingFallback: (pollingFallback: MessengerPollingFallback) => void;

  reset: () => void;
};

// This long type is unfortunately necessary so that `create<MessengerContext>` will typecheck.
const initialState: Pick<
  MessengerContext,
  | "hasInitialized"
  | "user"
  | "brand"
  | "brandContext"
  | "latestMessageUpdatedAt"
  | "retailerContext"
  | "query"
  | "lastSearchQuery"
  | "allMainStreamFetchState"
  | "unreadOnlyMainStreamFetchState"
  | "starredByBrandOnlyMainStreamFetchState"
  | "currentConversationIdentifier"
  | "isLoadingCount"
  | "showOnlyUnreadConversations"
  | "showOnlyStarredByBrandConversations"
  | "hiddenConversations"
  | "totalUnreadConversations"
  | "totalStarredByBrandConversations"
  | "wasConnected"
  | "pollingFallback"
> = {
  hasInitialized: false,
  user: undefined,
  brand: undefined,
  brandContext: undefined,
  latestMessageUpdatedAt: undefined,
  retailerContext: undefined,
  query: "",
  lastSearchQuery: undefined,
  allMainStreamFetchState: createMainStreamFetchState(),
  unreadOnlyMainStreamFetchState: createMainStreamFetchState(),
  starredByBrandOnlyMainStreamFetchState: createMainStreamFetchState(),
  currentConversationIdentifier: undefined,
  isLoadingCount: 0,
  showOnlyUnreadConversations: false,
  showOnlyStarredByBrandConversations: false,
  hiddenConversations: [],
  totalUnreadConversations: undefined,
  totalStarredByBrandConversations: undefined,
  wasConnected: false,
  pollingFallback: undefined,
};

export const { useStoreOrUndefined: useMessengerContext, Provider } =
  createContextStoreV2<MessengerContext>((set) => ({
    ...initialState,
    setHasInitialized: (hasInitialized: boolean) => set({ hasInitialized }),
    setUser: (user: IUser) => set({ user }),
    setBrandContext: (
      updater: (
        currentBrandContext?: IGetMessengerConversationsResponse.ISimpleBrand
      ) => IGetMessengerConversationsResponse.ISimpleBrand | undefined
    ) => set((state) => ({ brandContext: updater(state.brandContext) })),
    setLatestMessageUpdatedAt: (latestMessageUpdatedAt: number | undefined) =>
      set({ latestMessageUpdatedAt }),
    setRetailerContext: (
      updater: (currentRetailerContext?: IRetailer) => IRetailer | undefined
    ) => set((state) => ({ retailerContext: updater(state.retailerContext) })),
    setQuery: (query: string) => set({ query }),
    setLastSearchQuery: (lastSearchQuery: string | undefined) =>
      set({ lastSearchQuery }),
    setAllMainStreamFetchState: (updater) =>
      set((state) => ({
        allMainStreamFetchState: updater(state.allMainStreamFetchState),
      })),
    setUnreadOnlyMainStreamFetchState: (updater) =>
      set((state) => ({
        unreadOnlyMainStreamFetchState: updater(
          state.unreadOnlyMainStreamFetchState
        ),
      })),
    setStarredByBrandOnlyMainStreamFetchState: (updater) =>
      set((state) => ({
        starredByBrandOnlyMainStreamFetchState: updater(
          state.starredByBrandOnlyMainStreamFetchState
        ),
      })),
    setSearchResults: (searchResults: Set<string> | undefined) =>
      set({ searchResults }),
    setCurrentConversationIdentifier: (
      currentConversationIdentifier: string | undefined
    ) => set({ currentConversationIdentifier }),
    incrementIsLoadingCount: () =>
      set((state) => ({ isLoadingCount: state.isLoadingCount + 1 })),
    decrementIsLoadingCount: () =>
      set((state) => ({ isLoadingCount: state.isLoadingCount - 1 })),
    setShowOnlyUnreadConversations: (showOnlyUnreadConversations: boolean) =>
      set({ showOnlyUnreadConversations }),
    setShowOnlyStarredByBrandConversations: (
      showOnlyStarredByBrandConversations: boolean
    ) => set({ showOnlyStarredByBrandConversations }),
    setTotalUnreadConversations: (
      newTotalUnreadConversations:
        | number
        | undefined
        | ((
            previousTotalUnreadConversations: number | undefined
          ) => number | undefined)
    ) =>
      set(({ totalUnreadConversations: previousTotalUnreadConversations }) => ({
        totalUnreadConversations:
          typeof newTotalUnreadConversations === "function"
            ? newTotalUnreadConversations(previousTotalUnreadConversations)
            : newTotalUnreadConversations,
      })),
    setTotalStarredByBrandConversations: (
      totalStarredByBrandConversations: number | undefined
    ) => set({ totalStarredByBrandConversations }),
    setWasConnected: (wasConnected: boolean) => set({ wasConnected }),
    setPollingFallback: (pollingFallback: MessengerPollingFallback) =>
      set({ pollingFallback }),
    reset: () => set(initialState),
  }));
